// "Our products" (after Attio's Build pipeline / Convert leads list): the three products
// sit in a sticky list on the left, and the one you are reading lights up. Each has one
// statement and one short moving picture that shows the product doing its job in about
// five seconds.
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Copy, Download, EyeOff, FileText, Globe, HardDrive, Mail, Sparkles } from "lucide-react";
import { SmartLink } from "@/components/Header";
import { integrationByKey } from "@/data/site";
import { EASE, Rise, WindowDots, useStory, useTyped } from "./motion";

const INBOX = "/brand/inbox-mark-256.png";
const AGENTS = "/brand/agents-mark-256.png";
const MEET = "/brand/meet-mark-256.png";
const BLUE = "hsl(216 84% 52%)";

const panel = "rounded-2xl border border-[hsl(0_0%_88%)] bg-background shadow-[0_24px_50px_-30px_hsl(216_50%_20%/0.35)]";

/* ── Inbox: a new email runs through a workflow and lands in Slack ────────── */
const FLOW = [
  { app: "gmail", label: "When a new email arrives" },
  { app: null, label: "Summarise it" },
  { app: "slack", label: "Post it to #finance in Slack" },
];

const InboxMock = () => {
  const { ref, step } = useStory(5, 900, 2600);
  return (
    <div ref={ref} aria-hidden className="grid items-start gap-5 md:grid-cols-[0.9fr_1.1fr]">
      <div className={`${panel} p-5`}>
        <div className="flex items-center justify-between">
          <p className="text-[15px] font-semibold text-foreground">Workflow</p>
          <span className="flex items-center gap-2 text-[13px] text-muted-foreground">
            On
            <span className="relative h-5 w-9 rounded-full bg-foreground">
              <span className="absolute right-0.5 top-0.5 h-4 w-4 rounded-full bg-background" />
            </span>
          </span>
        </div>
        <ol className="mt-5 space-y-2">
          {FLOW.map((f, i) => {
            const lit = step >= i + 1;
            const logo = f.app ? integrationByKey(f.app) : null;
            return (
              <li key={f.label}>
                <div
                  className={`flex items-center gap-3 rounded-xl border px-3.5 py-3 transition-all duration-300 ${
                    lit ? "border-foreground bg-background" : "border-border bg-background"
                  }`}
                >
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors duration-300 ${lit ? "bg-foreground text-background" : "bg-[hsl(216_40%_95%)] text-foreground"}`}>
                    {logo ? <img src={logo.logo} alt="" className={`h-4 w-4 object-contain brightness-0 ${lit ? "invert" : ""}`} /> : <Sparkles className="h-4 w-4" />}
                  </span>
                  <span className="text-[15px] font-medium text-foreground">{f.label}</span>
                </div>
                {i < FLOW.length - 1 && <span className="ml-[29px] block h-3 w-px bg-border" />}
              </li>
            );
          })}
        </ol>
      </div>

      <div className="space-y-4">
        <div className={`${panel} p-5 transition-all duration-500 ${step >= 0 ? "opacity-100" : "opacity-0"}`}>
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[hsl(40_9%_94%)]"><Mail className="h-4 w-4" /></span>
            <div className="min-w-0">
              <p className="text-[14px] font-semibold text-foreground">Dana, Acme Co</p>
              <p className="text-[13px] text-muted-foreground">New email · just now</p>
            </div>
          </div>
          <p className="mt-3 text-[16px] font-semibold text-foreground">Invoice #2041 is due on Friday</p>
          <p className="mt-1 text-[14px] leading-relaxed text-muted-foreground">Hi, a reminder that invoice #2041 for $4,200 is due this Friday. Could you confirm it is on its way? Thanks, Dana</p>
          <AnimatePresence>
            {step >= 2 && (
              <motion.p
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-[hsl(40_9%_95%)] px-2.5 py-1.5 text-[13px] font-medium text-foreground"
              >
                <Sparkles className="h-3.5 w-3.5" /> Summary: $4,200 due Friday, Acme Co
              </motion.p>
            )}
          </AnimatePresence>
        </div>
        <AnimatePresence>
          {step >= 3 && (
            <motion.div
              initial={{ opacity: 0, y: 14, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, ease: EASE }}
              className={`${panel} p-5`}
            >
              <p className="flex items-center gap-2 text-[14px] font-semibold text-foreground">
                <img src={integrationByKey("slack")!.logo} alt="" className="h-4 w-4 grayscale" /> # finance
              </p>
              <div className="mt-3 flex gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-foreground"><img src={INBOX} alt="" className="h-4 w-4 brightness-0 invert" /></span>
                <div>
                  <p className="text-[13px] font-semibold text-foreground">ClearMakes Inbox <span className="font-normal text-muted-foreground">just now</span></p>
                  <p className="text-[15px] leading-snug text-foreground">Acme Co: invoice #2041, $4,200, due Friday.</p>
                  {step >= 4 && (
                    <p className="mt-2 text-[13px] font-medium text-muted-foreground">
                      Done. Nothing for you to copy.
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

/* ── Agents: learns your documents, then answers on your website ─────────── */
const KNOW = [
  { icon: FileText, label: "12 documents" },
  { icon: Globe, label: "acme.com, 48 pages" },
  { icon: HardDrive, label: "Google Drive: Operations" },
];

const AgentsMock = () => {
  const { ref, step } = useStory(5, 1000, 2800);
  const answer = useTyped("Yes. Orders to Canada arrive in 5 to 7 working days.", step >= 2, 24);
  return (
    <div ref={ref} aria-hidden className="grid items-start gap-5 md:grid-cols-[0.85fr_1.15fr]">
      <div className="space-y-4">
        <div className={`${panel} p-5`}>
          <p className="text-[15px] font-semibold text-foreground">What your agent knows</p>
          <ul className="mt-4 space-y-2.5">
            {KNOW.map((k, i) => (
              <li key={k.label} className={`flex items-center gap-3 rounded-xl border border-border px-3 py-2.5 transition-all duration-500 ${i <= step + 1 ? "translate-y-0 opacity-100" : "translate-y-1 opacity-0"}`}>
                <k.icon className="h-4 w-4 shrink-0 text-foreground" />
                <span className="flex-1 text-[14.5px] text-foreground">{k.label}</span>
                
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl bg-[hsl(0_0%_7%)] p-5 font-mono text-[12.5px] text-[hsl(0_0%_85%)] shadow-[0_24px_50px_-30px_hsl(0_0%_0%/0.6)]">
          <div className="flex items-center justify-between font-sans">
            <p className="text-[14px] font-semibold text-[hsl(0_0%_97%)]">Put it on your website</p>
            <span className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] font-medium transition-colors duration-300 ${step >= 4 ? "bg-[hsl(0_0%_100%)] text-[hsl(0_0%_4%)]" : "bg-[hsl(0_0%_100%/0.1)] text-[hsl(0_0%_92%)]"}`}>
              <Copy className="h-3.5 w-3.5" /> {step >= 4 ? "Copied" : "Copy"}
            </span>
          </div>
          <p className="mt-3 break-all">&lt;script src="agents.clearmakes.com/agents.js"&gt;</p>
        </div>
      </div>

      <div className={`${panel} overflow-hidden`}>
        <div className="flex h-10 items-center gap-3 border-b border-border bg-[hsl(40_9%_98%)] px-4">
          <WindowDots />
          <span className="rounded-md bg-background px-2 py-0.5 text-[12px] text-muted-foreground">acme.com</span>
        </div>
        <div className="relative min-h-[330px] bg-[linear-gradient(180deg,hsl(40_9%_98%),hsl(216_60%_97%))] p-5">
          <div className="space-y-2">
            <span className="block h-3 w-40 rounded bg-[hsl(0_0%_88%)]" />
            <span className="block h-3 w-64 rounded bg-[hsl(0_0%_92%)]" />
            <span className="block h-3 w-52 rounded bg-[hsl(0_0%_92%)]" />
          </div>
          {/* the chat window on the visitor's side */}
          <div className="absolute bottom-4 right-4 w-[min(320px,calc(100%-32px))] rounded-2xl border border-border bg-background shadow-[0_20px_40px_-20px_hsl(0_0%_0%/0.35)]">
            <div className="flex items-center gap-2.5 border-b border-border px-4 py-3">
              <span className="flex h-6 w-6 items-center justify-center rounded-md bg-foreground"><img src={AGENTS} alt="" className="h-3.5 w-3.5 brightness-0 invert" /></span>
              <p className="text-[14px] font-semibold text-foreground">Acme help</p>
            </div>
            <div className="space-y-2.5 px-4 py-4">
              <AnimatePresence>
                {step >= 0 && (
                  <motion.p initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="ml-auto w-fit max-w-[85%] rounded-2xl rounded-br-md bg-[hsl(0_0%_7%)] px-3.5 py-2 text-[14.5px] text-[hsl(0_0%_100%)]">
                    Do you ship to Canada?
                  </motion.p>
                )}
              </AnimatePresence>
              {step === 1 && (
                <span className="flex w-fit gap-1 rounded-2xl bg-[hsl(40_9%_95%)] px-3.5 py-3">
                  {[0, 1, 2].map((i) => (
                    <motion.span key={i} className="h-1.5 w-1.5 rounded-full bg-[hsl(0_0%_45%)]" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15 }} />
                  ))}
                </span>
              )}
              {step >= 2 && (
                <div className="w-fit max-w-[90%]">
                  <p className="rounded-2xl rounded-bl-md bg-[hsl(40_9%_95%)] px-3.5 py-2 text-[14.5px] leading-snug text-foreground">{answer}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── ClearMeet: a question in a call, a short answer, the notes ──────────── */
const Tile = ({ initials, name, talking }: { initials: string; name: string; talking: boolean }) => (
  <div className={`relative flex aspect-[4/3] items-center justify-center rounded-xl bg-[linear-gradient(135deg,hsl(216_28%_24%),hsl(216_40%_12%))] transition-shadow duration-300 ${talking ? "shadow-[0_0_0_3px_hsl(216_84%_58%)]" : ""}`}>
    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(216_20%_78%)] text-[17px] font-bold text-[hsl(216_40%_15%)]">{initials}</span>
    <span className="absolute bottom-2 left-2.5 text-[12px] font-medium text-[hsl(0_0%_96%)]">{name}</span>
    <span className="absolute bottom-2.5 right-2.5 flex h-3.5 items-end gap-[2px]">
      {[0, 1, 2, 3].map((i) => (
        <span key={i} className={`w-[3px] rounded-full bg-[hsl(0_0%_96%)] ${talking ? "cm-bar" : "h-1"}`} style={{ animationDelay: `${i * 0.12}s` }} />
      ))}
    </span>
  </div>
);

const MeetMock = () => {
  const { ref, step } = useStory(5, 1000, 3000);
  const answer = useTyped("At $18 a seat, forty people come to $720 a month. The first 30 days are free.", step >= 3, 20);
  return (
    <div ref={ref} aria-hidden className="grid items-stretch gap-5 md:grid-cols-[1.2fr_0.8fr]">
      <div className="flex flex-col overflow-hidden rounded-2xl border border-[hsl(216_30%_20%)] bg-[hsl(216_35%_9%)] shadow-[0_24px_50px_-30px_hsl(216_50%_20%/0.5)]">
        <div className="flex h-10 items-center justify-between px-4">
          <WindowDots dark />
          <span className="text-[12px] text-[hsl(0_0%_70%)]">Weekly pilot review</span>
        </div>
        <div className="grid flex-1 grid-cols-2 gap-2.5 px-3 pb-3">
          <Tile initials="SK" name="Sam, Northwind" talking={step <= 1} />
          <Tile initials="You" name="You" talking={step >= 4} />
        </div>
        <div className="mx-3 mb-3 min-h-[44px] rounded-lg bg-[hsl(0_0%_0%/0.45)] px-3 py-2 text-[14px] text-[hsl(0_0%_96%)]">
          Sam: <span className="font-medium">What would a pilot cost for forty people?</span>
        </div>
      </div>

      <div className={`${panel} flex flex-col p-4`}>
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-[14px] font-semibold text-foreground"><img src={MEET} alt="" className="h-5 w-5 brightness-0" /> ClearMeet</span>
          <span className="rounded-full bg-[hsl(216_40%_94%)] px-2 py-0.5 text-[12px] font-semibold text-foreground">12:04</span>
        </div>
        <div className="mt-3 flex gap-1 rounded-lg bg-[hsl(40_9%_95%)] p-1 text-[12.5px] font-medium">
          {["Answers", "Summary", "Notes"].map((t, i) => (
            <span key={t} className={`flex-1 rounded-md py-1 text-center ${i === 0 ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"}`}>{t}</span>
          ))}
        </div>
        <div className="mt-4 flex-1">
          <p className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[hsl(216_84%_42%)]">Say this</p>
          <p className="mt-1.5 min-h-[72px] text-[16px] font-medium leading-snug text-foreground">
            {step >= 3 ? answer : step === 2 ? <span className="text-muted-foreground">Listening…</span> : <span className="text-muted-foreground">Waiting for a question</span>}
          </p>
          <div className={`mt-4 rounded-xl border border-border p-3 transition-opacity duration-300 ${step >= 4 ? "opacity-100" : "opacity-0"}`}>
            <p className="text-[12px] font-semibold text-muted-foreground">Action item</p>
            <p className="mt-0.5 text-[14px] text-foreground">Send the price list after the call</p>
          </div>
        </div>
        <p className="mt-4 flex items-center gap-1.5 text-[12.5px] text-muted-foreground"><EyeOff className="h-3.5 w-3.5" /> Only you see this. Hidden when you share your screen.</p>
      </div>
    </div>
  );
};

/* ── The section ──────────────────────────────────────────────────────────── */
const ITEMS = [
  {
    key: "inbox",
    name: "ClearMakes Inbox",
    logo: INBOX,
    lead: "Your messages, handled.",
    rest: "Workflows act on every new email and chat: they summarise it, draft the reply or post it to Slack, while you just check the results.",
    href: "/products/inbox",
    Mock: InboxMock,
  },
  {
    key: "agents",
    name: "ClearMakes Agents",
    logo: AGENTS,
    lead: "Answers from your own documents.",
    rest: "Agents learn your files, your website and Google Drive, then answer your team and the visitors on your website.",
    href: "https://agents.clearmakes.com",
    Mock: AgentsMock,
  },
  {
    key: "meet",
    name: "ClearMeet",
    logo: MEET,
    lead: "The right answer, mid-meeting.",
    rest: "ClearMeet hears the question and shows you a short answer in seconds, then writes the notes. No bot joins the call, and it stays hidden when you share your screen.",
    href: "https://meet.clearmakes.com",
    download: "https://meet.clearmakes.com/download",
    Mock: MeetMock,
  },
];

export const ProductTour = () => {
  const [active, setActive] = useState(ITEMS[0].key);
  const refs = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) if (e.isIntersecting) setActive((e.target as HTMLElement).dataset.key!);
      },
      { rootMargin: "-45% 0px -50% 0px" },
    );
    Object.values(refs.current).forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, []);

  const go = (key: string) => refs.current[key]?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <section id="products" aria-labelledby="products-h" className="scroll-mt-28 border-b border-[hsl(216_30%_86%)] bg-[hsl(216_60%_96.5%)]">
      {/* One plain paragraph that says what the company is. Search engines and AI tools
          quote paragraphs like this, so it stays true, short and able to stand alone. */}
      <div className="mx-auto max-w-[1400px] border-b border-[hsl(216_30%_86%)] px-5 py-14 sm:px-12 sm:py-16 lg:border-x xl:px-16">
        <Rise>
          <h2 id="products-h" className="text-[13px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">What is ClearMakes?</h2>
          <p className="mt-4 max-w-[980px] text-[1.3rem] leading-[1.45] tracking-[-0.01em] text-foreground sm:text-[1.6rem]">
            ClearMakes makes three AI products for small teams. <strong className="font-semibold">ClearMakes Inbox</strong> runs workflows on new email and chat messages and answers everyday questions. <strong className="font-semibold">ClearMakes Agents</strong> answers your team and your website visitors from your own documents. <strong className="font-semibold">ClearMeet</strong> gives live answers and notes in meetings on Windows. One login opens all three, and each is free to start.
          </p>
        </Rise>
      </div>
      <div className="mx-auto grid max-w-[1400px] border-[hsl(216_30%_86%)] lg:grid-cols-[320px_1fr] lg:border-x xl:grid-cols-[360px_1fr]">
        <aside className="hidden border-r border-[hsl(216_30%_86%)] lg:block">
          <div className="sticky top-[128px] px-10 py-20 xl:px-14">
            <p className="text-[13px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Our products</p>
            <ul className="mt-6 space-y-1">
              {ITEMS.map((it) => (
                <li key={it.key}>
                  <button
                    type="button"
                    onClick={() => go(it.key)}
                    aria-current={active === it.key}
                    className={`relative flex w-full items-center gap-3 py-2.5 pl-6 text-left text-[20px] font-medium tracking-[-0.015em] transition-colors duration-300 ${
                      active === it.key ? "text-foreground" : "text-[hsl(216_12%_62%)] hover:text-[hsl(216_20%_35%)]"
                    }`}
                  >
                    {active === it.key && (
                      <motion.span layoutId="tour-bar" className="absolute bottom-1 left-0 top-1 w-[3px] rounded-full" style={{ background: BLUE }} transition={{ type: "spring", stiffness: 380, damping: 34 }} />
                    )}
                    <img src={it.logo} alt="" className={`h-6 w-6 brightness-0 transition-opacity duration-300 ${active === it.key ? "" : "opacity-30"}`} />
                    {it.name}
                  </button>
                </li>
              ))}
            </ul>
            <SmartLink href="/products" className="group mt-10 inline-flex items-center gap-1.5 text-[15px] font-medium text-foreground">
              Compare all products <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </SmartLink>
          </div>
        </aside>

        <div>
          {ITEMS.map((it, i) => (
            <article
              key={it.key}
              id={`product-${it.key}`}
              data-key={it.key}
              ref={(el) => (refs.current[it.key] = el)}
              aria-labelledby={`${it.key}-h`}
              className={`scroll-mt-28 ${i > 0 ? "border-t border-[hsl(216_30%_86%)]" : ""}`}
            >
              <div className="px-5 py-16 sm:px-12 sm:py-20 xl:px-16">
                <Rise>
                  <p className="flex items-center gap-2.5 text-[15px] font-semibold text-foreground lg:hidden">
                    <img src={it.logo} alt="" className="h-6 w-6 brightness-0" /> {it.name}
                  </p>
                  <h3 id={`${it.key}-h`} className="mt-4 max-w-[880px] text-[1.9rem] font-semibold leading-[1.18] tracking-[-0.03em] text-foreground sm:text-[2.5rem] lg:mt-0">
                    {it.lead} <span className="text-[hsl(216_15%_52%)]">{it.rest}</span>
                  </h3>
                  <div className="mt-8 flex flex-wrap items-center gap-3">
                    <SmartLink href={it.href} className="group inline-flex h-11 items-center gap-2 rounded-xl bg-[hsl(0_0%_4%)] px-5 text-[15px] font-semibold text-background transition-colors hover:bg-[hsl(216_84%_52%)]">
                      Explore {it.name.replace("ClearMakes ", "")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </SmartLink>
                    {it.download && (
                      <a href={it.download} className="inline-flex h-11 items-center gap-2 rounded-xl border border-[hsl(216_30%_80%)] bg-background px-5 text-[15px] font-medium text-foreground transition-colors hover:bg-[hsl(216_60%_94%)]">
                        <Download className="h-4 w-4" /> Download for Windows
                      </a>
                    )}
                  </div>
                </Rise>
              </div>
              <div className="relative overflow-hidden border-t border-[hsl(216_30%_86%)] bg-[linear-gradient(150deg,hsl(216_84%_52%),hsl(222_72%_24%))] px-4 py-12 sm:px-10 sm:py-16 xl:px-16">
                <div aria-hidden className="cm-grid-dark pointer-events-none absolute inset-0 opacity-60" />
                <div className="relative">
                  <it.Mock />
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

